<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
// Zimobi: the same answer formatting as the card detail page.
import {
  formatDateTime,
  formatFieldValue,
} from 'custom/dashboard/routes/dashboard/cards/formatFieldValue';
import CardTaskStatusBadge from 'custom/dashboard/routes/dashboard/cards/components/CardTaskStatusBadge.vue';
// Zimobi: the task's own row and answers from the card page, worked in place.
import CardTaskRow from 'custom/dashboard/routes/dashboard/cards/components/CardTaskRow.vue';
import CardTaskAnswers from 'custom/dashboard/routes/dashboard/cards/components/CardTaskAnswers.vue';
// Zimobi: a visit request shows, and schedules, its visit as the card page does.
import CardVisitSteps from 'custom/dashboard/routes/dashboard/cards/components/CardVisitSteps.vue';
import ScheduleVisitDialog from 'custom/dashboard/routes/dashboard/cards/components/ScheduleVisitDialog.vue';
import { isVisitRequest } from 'custom/dashboard/routes/dashboard/cards/visitTask';
// Zimobi: the property the request is about, where the agent sees it first.
import CardPropertyCode from 'custom/dashboard/routes/dashboard/cards/components/CardPropertyCode.vue';
import { useCardTask } from 'custom/dashboard/routes/dashboard/cards/useCardTask';
import { taskFromPayload } from 'custom/dashboard/routes/dashboard/cards/taskFromPayload';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

const { contentAttributes } = useMessageContext();
const route = useRoute();
const router = useRouter();

// MessageList camelizes every message deeply before handing it to Message.vue, so the
// payload arrives as `card`, and each schema entry's keys are camelized too.
const card = computed(() => contentAttributes.value?.card || {});

// The type's form_schema travels with the message (see CardTasks::AfterCreateService), so the
// bubble labels the values without fetching the card type.
const fields = computed(() => card.value.fields || []);
// Zimobi: snake-cased back, since MessageList camelized the answer keys along with the rest.
const formData = computed(() => useSnakeCase(card.value.formData || {}));

// Zimobi: never shown in the bubble: the conversation already identifies the requester by them.
// The card keeps all the answers.
const HIDDEN_FIELD_TYPES = ['phone', 'email'];
const shownFields = list =>
  list.filter(field => !HIDDEN_FIELD_TYPES.includes(field.type));

// Only the fields the requester actually filled in: an empty optional field would render as
// a label with nothing under it.
const filledFields = computed(() =>
  shownFields(fields.value)
    .map(field => ({
      key: field.key,
      label: field.label || field.key,
      value: formatFieldValue(field, formData.value[field.key]),
    }))
    .filter(field => field.value !== '')
);

// A bubble written before tasks existed carries only the card; it still opens that card.
const openCard = () =>
  router.push({
    name: 'cards_index',
    params: { accountId: route.params.accountId },
    query: { card: card.value.cardId, task: card.value.taskId },
  });

// The type's name for the status comes resolved (see CardTasks::MessagePayload); a bubble written
// before the payload carried a status shows none.
const statusLabels = computed(() => ({
  [card.value.status]: card.value.statusLabel,
}));

// Zimobi: the live task, so its status, assignee and due date can be changed from the bubble.
// Reloaded when the payload says it changed (CardTasks::MessagePayload rewrites it on a status or
// due date change). Until it loads, or when the agent cannot read it, the bubble shows the payload.
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const { task, load, update, changeStatus, scheduleVisit } = useCardTask();
const scheduleDialogRef = ref(null);

// Zimobi: the block is drawn from the payload until the task is read, so it never changes size when
// the read lands; only then can it be worked. A bubble written before the payload carried the task
// and its status has nothing to draw it from.
const payloadTask = computed(() =>
  card.value.taskId && card.value.status ? taskFromPayload(card.value) : null
);
const shownTask = computed(() => task.value || payloadTask.value);

watch(
  () => [
    card.value.cardId,
    card.value.taskId,
    card.value.status,
    card.value.dueAt,
    // Zimobi: the visit a request was scheduled into changes on its own.
    card.value.visit?.status,
    card.value.visit?.dueAt,
  ],
  ([cardId, taskId]) => {
    // Zimobi: a deleted task reads as not found; its payload already says all there is.
    if (cardId && taskId && !card.value.deleted) load(cardId, taskId);
  },
  { immediate: true }
);

// The assignee picker lists the account's agents.
onMounted(() => {
  if (!agents.value.length) store.dispatch('agents/get');
});

const propertyCode = computed(
  () => shownTask.value?.data?.property_code || formData.value.property_code
);

const dueAt = computed(() =>
  card.value.dueAt ? formatDateTime(card.value.dueAt, 'dd/MM/yyyy HH:mm') : ''
);
</script>

<template>
  <BaseBubble class="p-0 min-w-0" data-bubble-name="card">
    <!-- Zimobi: a deleted task leaves only its name; its answers and actions leave the conversation. -->
    <div
      v-if="card.deleted"
      class="flex items-center gap-2 px-3 py-2.5 w-80 max-w-full text-sm text-n-slate-11"
      data-testid="card-task-deleted"
    >
      <span
        :class="card.typeIcon || 'i-lucide-clipboard-list'"
        class="size-4 shrink-0"
      />
      <span class="min-w-0 truncate line-through">{{ card.name }}</span>
      <span class="text-xs shrink-0 ltr:ml-auto rtl:mr-auto">
        {{ $t('CARDS.TASKS.DELETED') }}
      </span>
    </div>
    <!-- Zimobi: the task block of the card page, worked in place, with its answers always open. Drawn
         from the payload until the task is read (see shownTask). -->
    <div
      v-else-if="shownTask"
      class="flex flex-col w-[28rem] max-w-full"
      data-testid="card-task-live"
      :data-loaded="!!task"
    >
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5"
        :class="{ 'pointer-events-none': !task }"
        data-testid="card-task-row"
      >
        <CardTaskRow
          :task="shownTask"
          :agents="agents"
          @update="(target, attributes) => update(attributes, target)"
          @status="(target, transition) => changeStatus(transition, target)"
          @schedule="scheduleDialogRef.open(task)"
        />
      </div>
      <div v-if="propertyCode" class="px-3 pb-2.5 -mt-1">
        <CardPropertyCode :code="propertyCode" />
      </div>
      <div class="flex flex-col gap-3 px-3 pt-3 pb-4 border-t border-n-weak">
        <CardVisitSteps v-if="isVisitRequest(shownTask)" :task="shownTask" />
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('CARDS.TASKS.ANSWERS') }}
        </h3>
        <CardTaskAnswers
          :fields="shownFields(shownTask.fields)"
          :data="shownTask.data"
        />
      </div>
      <div class="px-3">
        <button
          type="button"
          class="flex items-center justify-between w-full h-9 px-3 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="card-open"
          @click="openCard"
        >
          {{ $t('CARDS.MESSAGE.OPEN') }}
          <span class="i-lucide-arrow-right size-4 text-n-slate-11" />
        </button>
      </div>
      <ScheduleVisitDialog
        ref="scheduleDialogRef"
        :agents="agents"
        :submit="scheduleVisit"
      />
    </div>
    <!-- Zimobi: the same header, answer grid and tokens as the card modal the bubble opens. -->
    <div v-else class="flex flex-col gap-3 px-3 pt-3 w-80 max-w-full">
      <div class="flex items-start gap-3">
        <span
          class="flex items-center justify-center rounded-lg size-8 shrink-0 bg-n-alpha-2 text-n-slate-11"
        >
          <span
            :class="card.typeIcon || 'i-lucide-clipboard-list'"
            class="size-4"
          />
        </span>
        <div class="flex-1 min-w-0">
          <p
            class="flex items-center gap-1.5 mb-0 text-xs text-n-slate-11"
            data-testid="card-task"
          >
            <template v-if="card.taskId">
              {{ $t('CARDS.MESSAGE.TASK', { id: card.taskId }) }}
            </template>
            <template v-else>{{ $t('CARDS.MESSAGE.BADGE') }}</template>
            <span
              v-if="dueAt"
              class="inline-flex items-center gap-1"
              data-testid="card-due-at"
            >
              <span class="i-lucide-calendar-clock size-3" />
              <!-- Zimobi: the agency's deadline, not a time the requester picked. -->
              {{ $t('CARDS.MESSAGE.DUE_AT', { date: dueAt }) }}
            </span>
          </p>
          <h4 class="mb-0 text-sm font-medium leading-5 text-n-slate-12">
            {{ card.typeName }}
          </h4>
          <CardPropertyCode
            v-if="propertyCode"
            :code="propertyCode"
            class="mt-1"
          />
        </div>
        <CardTaskStatusBadge
          v-if="card.status"
          :status="card.status"
          :labels="statusLabels"
        />
      </div>

      <dl
        v-if="filledFields.length"
        class="grid grid-cols-2 p-3 mb-0 border gap-x-4 gap-y-3 rounded-xl border-n-weak"
      >
        <div
          v-for="field in filledFields"
          :key="field.key"
          class="flex flex-col gap-0.5 min-w-0"
          :class="{ 'col-span-2': field.key === 'description' }"
          :data-testid="`card-field-${field.key}`"
        >
          <dt class="text-xs text-n-slate-11">{{ field.label }}</dt>
          <dd class="mb-0 text-sm break-words text-n-slate-12">
            {{ field.value }}
          </dd>
        </div>
      </dl>

      <button
        v-if="card.cardId"
        type="button"
        class="flex items-center justify-between w-full h-9 px-3 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        data-testid="card-open"
        @click="openCard"
      >
        {{ $t('CARDS.MESSAGE.OPEN') }}
        <span class="i-lucide-arrow-right size-4 text-n-slate-11" />
      </button>
    </div>
  </BaseBubble>
</template>
